"use client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormEvent, useState } from "react";
import { motion } from "framer-motion";
import { ContactPageData } from "@/types/index";
import Link from "next/link";

interface ContactFormClientProps {
  contactPageData: ContactPageData;
  lang: string;
  submitContactForm: (formData: FormData) => Promise<{ success: boolean; error?: string }>;
}

export default function ContactFormClient({ contactPageData, lang, submitContactForm }: ContactFormClientProps) {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
    agreeToPrivacy: false,
  });

  // Form validation and submission states
  const [formErrors, setFormErrors] = useState<{
    fullName?: string;
    email?: string;
    message?: string;
    agreeToPrivacy?: string;
  }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form handling functions
  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (formErrors[field as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    // Clear submit error and success states
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  const validateForm = () => {
    const errors: typeof formErrors = {};

    // Validate required fields
    if (!formData.fullName.trim()) {
      errors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.message.trim()) {
      errors.message = "Message is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !formData.agreeToPrivacy) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Create FormData for server action
      const formDataToSubmit = new FormData();
      formDataToSubmit.append("fullName", formData.fullName);
      formDataToSubmit.append("email", formData.email);
      formDataToSubmit.append("subject", formData.subject);
      formDataToSubmit.append("message", formData.message);

      // Call server action
      const result = await submitContactForm(formDataToSubmit);

      if (result.success) {
        // Success
        setSubmitSuccess(true);
        setFormData({
          fullName: "",
          email: "",
          subject: "",
          message: "",
          agreeToPrivacy: false,
        });
        setFormErrors({});
      } else {
        setSubmitError(result.error || "Failed to submit form. Please try again.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : "Failed to submit form. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id='inquiries' className="w-full lg:w-auto lg:flex-1 lg:min-w-[500px] lg:max-w-2xl">
      <div
        className="bg-white rounded-lg p-6 sm:p-8 lg:p-12"
        style={{ boxShadow: "0 30px 60px 0 rgba(0, 0, 0, 0.08)" }}
      >
        <div className="flex flex-col gap-12">
          {/* Form Header */}
          <div className="flex flex-col gap-4">
            <h2 className="text-[#1A1D21] font-inter text-2xl sm:text-3xl lg:text-4xl font-medium leading-8 tracking-[-0.96px]">
              {contactPageData?.contact?.form?.title ||
                "Schedule an appointment"}
            </h2>
            <div className="flex flex-col">
              <p className="text-[#4F5662] font-inter text-lg leading-7">
                {contactPageData?.contact?.form?.description ||
                  "Call us 24/7 with any emergency or to schedule an appointment"}
              </p>
            </div>
          </div>

          {/* Form */}
          <form
            className="flex flex-col gap-8"
            onSubmit={handleSubmit}
          >
            {/* Name and Email Row */}
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
              <div className="flex-1 flex flex-col gap-3.5">
                <label className="text-[#1A1D21] flex gap-1 font-inter text-base font-medium leading-4 capitalize">
                  <span className="line-clamp-1">
                    {contactPageData?.contact?.form?.labels
                      ?.fullName || "Full Name"}
                  </span>
                  <span className="text-[#F00]">*</span>
                </label>
                <span>
                  {" "}
                  <input
                    type="text"
                    placeholder={
                      contactPageData?.contact?.form?.placeholders
                        ?.fullName || "Enter full name"
                    }
                    value={formData.fullName}
                    onChange={(e) =>
                      handleInputChange("fullName", e.target.value)
                    }
                    className={`w-full px-4.5 py-4.5 border rounded-lg bg-white text-[#1A1D21] leading-4 placeholder-[#88909F] focus:outline-none transition-colors ${
                      formErrors.fullName
                        ? "border-red-500 focus:border-red-500"
                        : "border-[#DEE1E5] focus:border-[#1A5852]"
                    }`}
                  />
                  {formErrors.fullName && (
                    <p className="text-red-500 text-sm mt-1">
                      {formErrors.fullName}
                    </p>
                  )}
                </span>
              </div>
              <div className="flex-1 flex flex-col gap-3.5">
                <label className="text-[#1A1D21] flex gap-1 font-inter text-base font-medium leading-4 capitalize">
                  <span className="line-clamp-1">
                    {contactPageData?.contact?.form?.labels?.email ||
                      "Email Address"}
                  </span>
                  <span className="text-[#F00]">*</span>
                </label>
                <span>
                  <input
                    type="email"
                    placeholder={
                      contactPageData?.contact?.form?.placeholders
                        ?.email || "Enter email address"
                    }
                    value={formData.email}
                    onChange={(e) =>
                      handleInputChange("email", e.target.value)
                    }
                    className={`w-full px-4.5 py-4.5 border rounded-lg bg-white text-[#1A1D21] leading-4 placeholder-[#88909F] focus:outline-none transition-colors ${
                      formErrors.email
                        ? "border-red-500 focus:border-red-500"
                        : "border-[#DEE1E5] focus:border-[#1A5852]"
                    }`}
                  />
                  {formErrors.email && (
                    <p className="text-red-500 text-sm mt-1">
                      {formErrors.email}
                    </p>
                  )}
                </span>
              </div>
            </div>

            {/* Subject */}
            <div className="flex flex-col gap-3.5">
              <label className="text-[#1A1D21] font-inter text-base font-medium leading-4 capitalize">
                {contactPageData?.contact?.form?.labels?.subject ||
                  "Subject"}
              </label>

              <Select
                value={formData.subject}
                onValueChange={(value) =>
                  handleInputChange("subject", value)
                }
              >
                <SelectTrigger className="w-full px-4.5 py-4.5 border border-[#DEE1E5] rounded-lg bg-white text-[#1A1D21] focus:border-[#1A5852] focus:outline-none focus:ring-0 transition-colors data-[placeholder]:text-[#88909F]">
                  <SelectValue
                    placeholder={
                      contactPageData?.contact?.form?.placeholders
                        ?.subject || "Select…"
                    }
                  />
                </SelectTrigger>
                <SelectContent className="bg-white border border-[#DEE1E5] rounded-lg shadow-lg z-50">
                  {contactPageData?.contact?.form?.subjects?.map(
                    (subject) => (
                      <SelectItem
                        key={subject.id}
                        value={subject.value}
                      >
                        {subject.label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Message */}
            <div className="flex flex-col gap-3.5">
              <label className="text-[#1A1D21] font-inter text-base font-medium leading-4 capitalize">
                {contactPageData?.contact?.form?.labels?.message ||
                  "Message"}{" "}
                <span className="text-[#F00]">*</span>
              </label>
              <span>
                <textarea
                  rows={5}
                  placeholder={
                    contactPageData?.contact?.form?.placeholders
                      ?.message || "Write here"
                  }
                  value={formData.message}
                  onChange={(e) =>
                    handleInputChange("message", e.target.value)
                  }
                  className={`w-full px-6 py-5.25 border rounded-lg bg-white text-[#1A1D21] placeholder-[#88909F] focus:outline-none transition-colors resize-none ${
                    formErrors.message
                      ? "border-red-500 focus:border-red-500"
                      : "border-[#DEE1E5] focus:border-[#1A5852]"
                  }`}
                />
                {formErrors.message && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.message}
                  </p>
                )}
              </span>
            </div>

            {/* Privacy Policy Checkbox */}
            <div
              className="flex items-start gap-3"
              onClick={() =>
                handleInputChange(
                  "agreeToPrivacy",
                  !formData.agreeToPrivacy
                )
              }
            >
              <div className="flex items-center pt-0.75">
                <div
                  className={`flex items-center justify-center w-5 h-5 rounded-md border-2 cursor-pointer transition-colors ${
                    formData.agreeToPrivacy
                      ? "bg-[#1A5852] border-[#1A5852]"
                      : "bg-white border-[#DEE1E5]"
                  }`}
                >
                  {formData.agreeToPrivacy && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.6693 3.5L5.2526 9.91667L2.33594 7"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              </div>
              <p className="flex-1 text-[#3D434C] font-inter text-base font-medium leading-6">
                I agree to the{" "}
                <Link
                  href={`/${lang}/privacy-policy`}
                  target="_blank"
                  className="text-[#1A5852] underline cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </Link>{" "}
                regarding the handling of personal information.
              </p>
            </div>

            {/* Success Message */}
            {submitSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="min-w-8 w-8 min-h-8 h-8 text-green-600 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  <p className="text-green-800 font-medium">
                    {contactPageData?.contact?.form?.successMessage ||
                      "Thank you! Your message has been sent successfully. We'll get back to you soon."}
                  </p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg
                    className="min-w-6 w-6 min-h-6 h-6 text-red-600 mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  <p className="text-red-800 font-medium">
                    {submitError}
                  </p>
                </div>
              </div>
            )}
            {/* Submit Button */}
            {!submitSuccess && (
              <button
                type="submit"
                disabled={isSubmitting || !formData.agreeToPrivacy}
                className={`flex items-center justify-center gap-2.5 px-6 py-4.25 border border-[#1A5852] rounded-full font-inter text-base font-medium leading-4 capitalize transition-all duration-200 group self-start ${
                  isSubmitting || !formData.agreeToPrivacy
                    ? "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : "bg-white text-[#1A5852] hover:bg-[#1A5852] hover:text-white cursor-pointer"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-4 w-4 text-current"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg
                      width="8"
                      height="14"
                      viewBox="0 0 8 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`transition-colors ${
                        isSubmitting || !formData.agreeToPrivacy
                          ? "fill-gray-400"
                          : "group-hover:fill-white fill-[#1A5852]"
                      }`}
                    >
                      <path d="M6.91598 7.37487L2.03865 12.2106C1.83065 12.4187 1.49731 12.4187 1.33065 12.2106L1.03865 11.9185C0.989752 11.8735 0.950722 11.8188 0.924023 11.758C0.897324 11.6971 0.883539 11.6314 0.883539 11.565C0.883539 11.4985 0.897324 11.4328 0.924023 11.3719C0.950722 11.3111 0.989752 11.2564 1.03865 11.2114L5.29198 7.00001L1.03865 2.83126C0.989537 2.78627 0.950323 2.73155 0.923495 2.67057C0.896667 2.6096 0.882812 2.5437 0.882812 2.47708C0.882812 2.41046 0.896667 2.34457 0.923495 2.2836C0.950323 2.22262 0.989537 2.1679 1.03865 2.12291L1.33198 1.83076C1.49865 1.62266 1.83198 1.62266 2.03998 1.83076L6.95865 6.66651C7.04277 6.76793 7.08517 6.89757 7.07725 7.02913C7.06933 7.16068 7.01167 7.28429 6.91598 7.37487Z" />
                    </svg>
                    {contactPageData?.contact?.form?.submitText ||
                      "Submit"}
                  </>
                )}
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
